/**
 * #359 — the Network page's "are you sure?" checks before a connection request goes
 * out. The server no longer refuses a second request to a person already connected
 * (one person, several orgs: the recipient picks which org each request is for), nor
 * one to a person with a request still unanswered (Q1: warn, don't refuse). So the
 * sender is warned here instead — from data the sender ALREADY holds (their own
 * connection list and outbox), never a server round trip that could read the
 * recipient's memberships.
 */
import type { ConnectionRow, OutgoingRequest } from '../client/index.js'

export interface ConnectWarnings {
  /** Connections whose PEER is reached at this address. */
  connectedWith: ConnectionRow[]
  /** This org's requests to this address still waiting for the recipient's pick. */
  pending: OutgoingRequest[]
}

/** The peer-side addresses a connection is known by. Deliberately NOT
 *  `initiatorEmail` / `accepterEmail` as-is: those are fixed request roles, so one of
 *  them is always THIS org's own member — matching it would warn "already connected"
 *  when you type a colleague's (or your own) address. The peer's audit address is what
 *  `theirDefaultContact` falls back to server-side. */
function peerAddresses(c: ConnectionRow): string[] {
  return [
    c.theirDefaultContact,
    c.theirDefaultContactExplicit,
    c.peerIdentity?.email,
    c.peerRenamed?.auditEmail,
    c.peerRenamed?.currentEmail,
  ].filter((e): e is string => typeof e === 'string' && e.length > 0).map((e) => e.toLowerCase())
}

export function connectWarnings(
  email: string,
  connections: readonly ConnectionRow[],
  outgoing: readonly OutgoingRequest[],
): ConnectWarnings {
  const typed = email.trim().toLowerCase()
  if (!typed) return { connectedWith: [], pending: [] }
  return {
    connectedWith: connections.filter((c) => peerAddresses(c).includes(typed)),
    // Only requests waiting on the RECIPIENT ("hasn't been answered"): one at
    // pending_initiator_confirm was answered and waits on this org's own confirm, which the
    // outbox already shows with its Confirm action (#359 scopes the warning this way).
    pending: outgoing.filter((o) => o.state === 'pending_recipient' && o.recipientEmail.toLowerCase() === typed),
  }
}

/** The sentence(s) the confirm shows, or null when there's nothing to warn about. */
export function connectWarningText(email: string, w: ConnectWarnings): string | null {
  const parts: string[] = []
  if (w.connectedWith.length > 0) {
    const names = [...new Set(w.connectedWith.map((c) => c.org.name))].join(', ')
    parts.push(`You already have a connection with ${email.trim()} (${names}) — send another for a different organization?`)
  }
  if (w.pending.length > 0) {
    parts.push(`You already sent a request to ${email.trim()} that hasn't been answered. Send another?`)
  }
  return parts.length > 0 ? parts.join('\n\n') : null
}
